import { AxiosError } from 'axios';
import { usePaystackPayment } from 'react-paystack';
import { PaystackProps } from 'react-paystack/dist/types';
import { useNavigate } from 'react-router-dom';
import {
  InitializeHubtelDirectCharge,
  InitializeHubtelPreapproval,
  generatePaymentLinkByPolicyNumber,
  generatePolicyPaymentObjects,
  getAutoDebitStatus,
  getPaymentLink,
  initializeHubtelPayment,
  initializeItConsortiumPayment,
  regeneratePolicyPaymentObjects,
  resetSinglePayment,
  sendPayNowNotification,
  toggleAutoDebitStatus
} from '../api/requests';
import { HomePolicy } from '../models/homePolicy/HomePolicy';
import { Policy } from '../models/policy/Policy';
import {
  HUBTEL_RESOURCE_PAYMENT_LINKS,
  MOBILE_MONEY_NETWORKS,
  PAYMENT_OPTIONS,
  PAYMENT_PROCESSORS,
  PAYMENT_RESOURCE_TYPE
} from '../resources/enums';
import {
  ICustomerPayments,
  IInitiateHubtelPaymentDTO,
  IPolicyPaymentData,
  IRequestResponse,
  IVerifyMobilePaymentDTO
} from '../resources/interfaces';
import { getPaymentDataFromPolicy, showToast } from '../utils/miscFunctions';
import ChoosePaymentModal from '../views/shared/payments/ChoosePaymentModal';
import MakePayment from '../views/shared/payments/MakePayment';
import VerifyPaymentOTP from '../views/shared/payments/VerifyPaymentOTP';
import useModal from './modalHook';
import useUserAccount from './userAccountHook';
import { useEffect, useState } from 'react';
import PhoneVerificationModal from '../views/shared/policies/auto/PhoneVerificationModal';
import HubtelPreapprovalModal from '../views/shared/payments/HubtelPreapprovalModal';
import ProvideMomoDetails from '../views/shared/payments/ProvideMomoDetails';
import VerifyHubtelPreapprovalOTP from '../views/shared/payments/VerifyHubtelPreapprovalOTP';
import RegeneratePaymentObjectsModal from '../views/shared/policies/auto/RegeneratePaymentObjectsModal';
import { IPayment } from '../models/payment/IPayment';
import ActionConfirmation from '../components/elements/ActionConfirmation';
import ApproveModal from '../views/shared/payments/PaymentApproval';
import useRoute from './routesHook';

interface usePaymentProps {
  paystackConfig: PaystackProps;
  policy: Policy | HomePolicy | undefined;
}

function usePayment(props: usePaymentProps) {
  const navigate = useNavigate();
  const { } = useUserAccount();
  const { getDashboardBase } = useRoute();
  const { updateModalStates, ModalComponent, closeModal } = useModal();

  const PaymentModalComponent = ModalComponent;

  const [paymentCompleted, setPaymentCompleted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const initializePayment = usePaystackPayment(props.paystackConfig);

  const choosePaymentService = () => {
    if (props.policy) {
      updateModalStates({
        show: true,
        children: (
          <ChoosePaymentModal
            policy={props.policy}
            onProceed={(paymentData?: IPolicyPaymentData, processor?: PAYMENT_PROCESSORS) => {
              if (processor === PAYMENT_PROCESSORS.HUBTEL) {
                if (paymentData) {
                  initializeHubtel({ paymentData: paymentData });
                }
              }

              if (processor === PAYMENT_PROCESSORS.IT_CONSORTIUM) {
                closeModal();
                showPhoneVerificationModal(paymentData?.phone ?? '');
              }
            }}
          />
        ),
        size: 'sm'
      });
    }
  };

  const choosePaymentMethod = (
    phone: string,
    policyData: Omit<IPolicyPaymentData, 'accountNumber' | 'network'>,
    showMomoForm?: boolean,
    resourceType?: PAYMENT_RESOURCE_TYPE
  ) => {
    if (phone) {
      updateModalStates({
        show: true,
        size: 'md'
      });
    }
  };

  const showPhoneVerificationModal = (phone: string) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <PhoneVerificationModal
          policy={props.policy}
          close={closeModal}
          onProceed={() => {
            initializeItConsortiumPayment(phone, props.policy?.ref ?? '').then((response: any) => {
              showToast.success(response?.message ?? 'Payment initiated successfully awaiting user confirmation.');
            });
            closeModal();
          }}
          phoneNumber={phone}
          isCustomer={false}
        />
      ),

      classes: '!p-0 r-[5px]',
      static: true
    });
  };

  const initializeHubtel = ({
    paymentData,
    resourceType = PAYMENT_RESOURCE_TYPE.AUTO_POLICY
  }: {
    paymentData: IPolicyPaymentData;
    resourceType?: PAYMENT_RESOURCE_TYPE;
  }) => {
    let url = '';
    if (resourceType === PAYMENT_RESOURCE_TYPE.AUTO_POLICY) {
      url = HUBTEL_RESOURCE_PAYMENT_LINKS.AUTO_POLICY;
    }

    if (resourceType === PAYMENT_RESOURCE_TYPE.HAILING_SERVICES) {
      url = HUBTEL_RESOURCE_PAYMENT_LINKS.HAILING_SERVICES;
    }

    if (resourceType === PAYMENT_RESOURCE_TYPE.HOME_POLICY) {
      url = HUBTEL_RESOURCE_PAYMENT_LINKS.HOME_POLICY;
    }

    const payload: IInitiateHubtelPaymentDTO = {
      initialAmount: paymentData?.initialAmount,
      accountNumber: paymentData.accountNumber ?? '',
      network: paymentData.network ?? '',
      policyNumber: paymentData.policyNumber
    };

    initializeHubtelPayment(payload).then((response: any) => {
      const payload: IVerifyMobilePaymentDTO = {
        otpCode: response?.data?.otpPrefix,
        policyNumber: paymentData.policyNumber,
        recurringInvoiceId: response?.data?.recurringInvoiceId,
        requestId: response?.data?.requestId
      };

      updateModalStates({
        show: true,
        children: (
          <VerifyPaymentOTP
            phone={paymentData.phone}
            data={payload}
            onProceed={() => {
              onPaymentSuccessful('hubtel', PAYMENT_OPTIONS.MOBILE_MONEY);
            }}
          />
        )
      });
    });
  };

  const handlePayment = async (
    paymentMethod: PAYMENT_OPTIONS,
    paymentData: IPolicyPaymentData,
    resourceType = PAYMENT_RESOURCE_TYPE.AUTO_POLICY
  ) => {
    try {
      if (paymentData.discountCode) {
        // await applyDiscount(paymentData.policyNumber, paymentData.discountCode);
      }

      switch (paymentMethod) {
        case PAYMENT_OPTIONS.CARD:
          initializePayment(onPaymentSuccessful, closeModal);
          break;

        case PAYMENT_OPTIONS.BANK:
          showBankTransfer();
          break;

        case PAYMENT_OPTIONS.MOBILE_MONEY:
          if (paymentData) {
            if (paymentData?.isFullPayment) {
              initializePayment(onPaymentSuccessful, closeModal);
            } else {
              initializeHubtel({
                paymentData,
                resourceType
              });
            }
          }
          break;
      }
    } catch (error: AxiosError | IRequestResponse | any) {
      showToast.error(error?.description);
    }
  };

  const onPaymentSuccessful = (paymentService = 'paystack', type = PAYMENT_OPTIONS.CARD) => { 
    if (paymentService === 'paystack') {
      showToast.success('Payment completed successfully');
    } else {
      showToast.success(
        'Payment initiated successfully. Ask customer to enter Momo code if prompted to complete payment.'
      );
    }
    setPaymentCompleted(true);
    // updateModalStates({
    //     show: true,
    //     // children: <PaymentComplete type={paymentService} />,
    //     static: true,
    //     showCloseButton: false,
    //     size: "4xl"
    // })
  };

  function showBankTransfer() {
    updateModalStates({
      show: true,
      // children: <BankTransfer
      //     onPayLater={() => payLater()}
      //     policy={props.policy} />,
      static: true,
      showCloseButton: true,
      size: '4xl'
    });
  }

  const choosePaymentOption = () => {
    if (props.policy) {
      const payment: ICustomerPayments | undefined = props.policy?.getFirstUnpaidPayment();
      updateModalStates({
        show: true,
        children: (
          <MakePayment
            policy={props.policy}
            onProceed={(paymentOption: 'send-email' | 'make-payment') => {
              closeModal();
              if (paymentOption === 'make-payment') {
                if (props.policy) {
                  const paymentData: IPolicyPaymentData = getPaymentDataFromPolicy(props.policy);
                  handlePayment(PAYMENT_OPTIONS.CARD, paymentData);
                }
              }

              if (payment && paymentOption === 'send-email') {
                sendPaymentNotification(payment);
              }
            }}
          />
        )
      });
    }
  };

  const sendPaymentNotification = (payment: ICustomerPayments) => {
    // const payment: ICustomerPayments | undefined = props.policy?.getFirstUnpaidPayment();
    if (payment) {
      sendPayNowNotification(payment?.id, payment?.productType?.key)
        .then((response: any) => {
          showToast.success(response?.message ?? 'Payment link notification sent successfully');
        })
        .catch((error: any) => {
          showToast.error(error?.description ?? 'Failed to send payment link notification.');
        });
    } else {
      showToast.error('Failed to send payment link notification. Try again later.');
    }
  };

  const InitiateHubtelDirectCharge = (payment: ICustomerPayments) => {
    // const payment: ICustomerPayments | undefined = props.policy?.getFirstUnpaidPayment();
    if (payment) {
      InitializeHubtelDirectCharge(payment?.id)
        .then((response: any) => {
          showToast.success(response?.message ?? 'Hubtel direct charge initiated successfully');
        })
        .catch((error: any) => {
          showToast.error(error?.description ?? 'Failed to initiate Hubtel direct charge.');
        });
    } else {
      showToast.error('Failed to initiate Hubtel direct charge. Try again later.');
    }
  };

  const showHubtelPreapprovalModal = () => {
    const policy: any = props.policy;
    if (policy) {
      updateModalStates({
        show: true,
        children: (
          <HubtelPreapprovalModal
            policy={policy}
            onClose={closeModal}
            onProceed={(data: { phone: string; network: MOBILE_MONEY_NETWORKS }) => {
              if (policy) {
                const paymentData = getPaymentDataFromPolicy(policy, data.phone, data.network);
                InitializeHubtelPreapproval(paymentData.network, paymentData.phone, paymentData.policyNumber)
                  .then((response: any) => {
                    showToast.success(response?.message ?? 'Request received! Pending preapproval');
                    closeModal();

                    if (response && response?.data?.verificationType === 'OTP') {
                      updateModalStates({
                        show: true,
                        children: (
                          <VerifyHubtelPreapprovalOTP
                            phone={paymentData.phone}
                            policyNumber={paymentData.policyNumber}
                            otpPrefix={response?.data?.otpPrefix}
                            onProceed={() => {
                              onPaymentSuccessful('hubtel', PAYMENT_OPTIONS.MOBILE_MONEY);
                              closeModal();
                            }}
                          />
                        )
                      });
                    }
                  })
                  .catch((error: any) => {
                    showToast.error(error?.description ?? 'Failed to initiate Hubtel Preapproval.');
                  });
              } else {
                showToast.error('Failed to initiate Hubtel preapproval. Try again later.');
              }
            }}
          />
        ),
        size: 'sm'
      });
    }
  };

  const showHubtelPreapprovalModalForOthers = (policyNumber: string, phoneNumber: string) => {
    updateModalStates({
      show: true,
      children: (
        <HubtelPreapprovalModal
          policy={undefined}
          phoneNumber={phoneNumber}
          onClose={closeModal}
          onProceed={(data: { phone: string; network: MOBILE_MONEY_NETWORKS }) => {
            InitializeHubtelPreapproval(data.network, data.phone, policyNumber)
              .then((response: any) => {
                showToast.success(response?.message ?? 'Request received! Pending preapproval');
                closeModal();

                if (response && response?.data?.verificationType === 'OTP') {
                  updateModalStates({
                    show: true,
                    children: (
                      <VerifyHubtelPreapprovalOTP
                        phone={data.phone}
                        policyNumber={policyNumber}
                        otpPrefix={response?.data?.otpPrefix}
                        onProceed={() => {
                          onPaymentSuccessful('hubtel', PAYMENT_OPTIONS.MOBILE_MONEY);
                          closeModal();
                        }}
                      />
                    )
                  });
                }
              })
              .catch((error: any) => {
                showToast.error(error?.description ?? 'Failed to initiate Hubtel Preapproval.');
              });
          }}
        />
      ),
      size: 'sm'
    });
  };

  const regeneratePaymentObjects = (paymentDate: string) => {
    if (props.policy?.ref) {
      regeneratePolicyPaymentObjects(props.policy?.ref, paymentDate)
        .then((response) => {
          showToast.success(response?.message ?? 'Payment schedule generated successfully.');
        })
        .catch((error: any) => {
          showToast.error(
            error.desrcription ?? 'Failed to generate payment schedules. Please try again later.'
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const generatePaymentObjects = (paymentDate: string) => {
    if (props.policy?.ref) {
      generatePolicyPaymentObjects(props.policy?.ref, paymentDate)
        .then((response) => {
          showToast.success(response?.message ?? 'Payment schedule generated successfully.');
        })
        .catch((error: any) => {
          showToast.error(error.desrcription ?? 'Failed to generate payment schedules. Please try again later.');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const showGeneratePaymentObjectsModal = (action: string, onProceed: () => void) => {
    updateModalStates({
      show: true,
      title: 'Regenerate Payments Schedule',
      size: 'md',
      children: (
        <RegeneratePaymentObjectsModal
          policy={props.policy}
          onClose={closeModal}
          onProceed={() => {
            onProceed();
            closeModal();
          }}
          action={action}
        />
      ),

      classes: '!p-0 r-[5px]',
      static: true
    });
  };

  const handleResetPayment = async (payment: IPayment, onProceed: () => void) => {
    updateModalStates({
      show: true,
      showCloseButton: false,
      size: 'md',
      children: (
        <ActionConfirmation
          action="Reset"
          modalName="Payment"
          type='warning'
          resourceName={'payment'}
          onClose={closeModal}
          onConfirm={async () => {
            try {
              const response = await resetSinglePayment(Number(payment.id))

              if (response) {
                closeModal();
                showToast.success(response?.message ?? 'Payment reset successfully');
                onProceed();
              }
            } catch (error: any) {
                closeModal();
              showToast.error(error.description ?? 'Failed to reset payment');
            }
          }}
        />
      )
    });
  };

  const showPaymentApprovalModal = (payment: IPayment, onProceed: () => void) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <ApproveModal
          onSubmitted={() => {
            onProceed();
            closeModal();
          }}
          onCancel={closeModal}
          data={payment}
          title={'Approve Payment Edit'}
        />
      ),
      classes: '!p-0 r-[5px]'
    });
  };

  const generatePaymentLink = (paymentId: string, onProceed: (response: any) => void) => {
    getPaymentLink(paymentId)
      .then((response) => {
        showToast.success(response?.message);
        setLoading(true);
        onProceed(response);
      })
      .catch((error: any) => {
        showToast.error(error.description ?? 'Failed to generate payment link. Please try again later.');
      })
      .finally(() => setLoading(false));
  };


  const generatePolicyLinkByPolicyId = async (policyNumber: string, onProceed: (response: any) => void) => {
    try {
      const response: any = await generatePaymentLinkByPolicyNumber(policyNumber)
      showToast.success(response?.message);
      setLoading(true);
      onProceed(response);
    } catch (error: any) {
      showToast.error(error.description ?? 'Failed to generate payment link. Please try again later.');
    } finally {
      setLoading(false);
    }
  }

  const handleToggleAutoDebitStatus = (active: boolean, applicationNumber: string, productType: string) => {
    toggleAutoDebitStatus(active ? false : true, applicationNumber, productType)
      .then((response: any) => {
        showToast.success(response?.message ?? 'Auto Debit status toggled successfully');
      })
      .catch((error: any) => {
        console.log("Error ", error);
        showToast.error(error.description ?? 'Failed to toggle auto debit status');
      });
  };

  const getPolicyAutoDebitStatus = async (applicationNumber: string) => {

    try {
      const response: any = await getAutoDebitStatus(applicationNumber)
      return response?.data?.active;
    } catch (error) {
      console.log(error);
    }
  };


  const handleOnlineFleetPayment = async (fleetNumber: string) => {
    initializePayment(() => {
      showToast.success('Payment completed successfully');
      // navigate(`${getDashboardBase()}/fleets/${fleetNumber}`, { replace: true });
    }, closeModal)
  }

  const handleOnlineSinglePolicyPayment = async (policyNumber: string) => {
    initializePayment(() => {
      showToast.success('Payment completed successfully');
      // navigate(`${getDashboardBase()}/policies/${policyNumber}?type=auto`, { replace: true });
    }, closeModal)

  }

  return {
    handlePayment,
    PaymentModalComponent,
    choosePaymentMethod,
    initializeHubtel,
    choosePaymentService,
    choosePaymentOption,
    sendPaymentNotification,
    InitiateHubtelDirectCharge,
    showHubtelPreapprovalModal,
    paymentCompleted,
    showGeneratePaymentObjectsModal,
    generatePaymentObjects,
    regeneratePaymentObjects,
    handleResetPayment,
    showPaymentApprovalModal,
    generatePaymentLink,
    handleToggleAutoDebitStatus,
    getPolicyAutoDebitStatus,
    handleOnlineFleetPayment,
    handleOnlineSinglePolicyPayment,
    generatePolicyLinkByPolicyId,
    showHubtelPreapprovalModalForOthers
  };
}

export default usePayment;
